import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  AlertCircle,
  Package,
  Star,
  TrendingUp,
  TrendingDown,
  Calendar,
  Upload,
  FileText,
  CheckCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  Activity,
  BarChart3,
  Users,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { config } from "../lib/config";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface ProductDetails {
  id: string;
  product_name: string;
  sku: string;
  brand: string | null;
  form: string;
  thc_pct: number | null;
  cbd_pct: number | null;
  pack_size: string | null;
  active: boolean;
  created_at: string;
}

interface ReviewStats {
  total_reviews: number;
  avg_stars: number;
  star_distribution: { [key: number]: number };
  repeat_purchase_yes: number;
  repeat_purchase_no: number;
  best_used_distribution: { day: number; night: number; both: number };
  avg_scent_score: number | null;
  avg_dryness_score: number | null;
  avg_taste_score: number | null;
  avg_appearance_score: number | null;
  avg_packaging_score: number | null;
}

interface Review {
  id: string;
  stars: number;
  title: string | null;
  body: string | null;
  submitted_at: string;
  repeat_purchase: boolean | null;
  best_used: 'day' | 'night' | 'both' | null;
  scent_score: number | null;
  dryness_score: number | null;
  taste_score: number | null;
  appearance_score: number | null;
  packaging_score: number | null;
  helpful_count: number;
  not_helpful_count: number;
}

interface TimelineDataPoint {
  date: string;
  avg_rating: number;
  review_count: number;
}

interface CertificateFile {
  url: string;
  name: string;
  uploaded_at: string;
  file_size?: number;
}

interface ClinicProductDetailProps {
  productId?: string;
}

export function ClinicProductDetail({ productId }: ClinicProductDetailProps) {
  const { profile } = useAuth();
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [timelineData, setTimelineData] = useState<TimelineDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState<CertificateFile | null>(null);
  const [uploadingCert, setUploadingCert] = useState(false);

  // Check if user is clinic
  if (!profile || profile.role !== 'clinic') {
    return (
      <div className="min-h-screen bg-background pt-24 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="mt-16">
            <Alert variant="destructive" className="border-destructive/30 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                </div>
                <AlertDescription className="flex-1">
                  <strong className="block mb-1">Access Denied</strong>
                  <p className="text-destructive/90">
                    This page is only accessible to clinic accounts.
                  </p>
                </AlertDescription>
              </div>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (productId) {
      loadProductData();
    }
  }, [productId]);

  const loadProductData = async () => {
    if (!productId || !profile.clinic_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // 🔧 DEVELOPMENT MODE - Use mock data
      if (config.devMode.enabled && config.devMode.mockData) {
        console.log('🔧 DEV MODE: Loading mock product detail data');
        
        const mockProduct: ProductDetails = {
          id: productId,
          product_name: 'Blue Dream',
          sku: 'MWL-001',
          brand: 'Premium Cannabis Co',
          form: 'flower',
          thc_pct: 22.5,
          cbd_pct: 0.5,
          pack_size: '10g',
          active: true,
          created_at: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
        };

        const mockReviews: Review[] = [
          {
            id: '1',
            stars: 5,
            title: 'Excellent product',
            body: 'Very effective for pain relief and relaxation. Great taste and aroma.',
            submitted_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            repeat_purchase: true,
            best_used: 'night',
            scent_score: 5,
            dryness_score: 4,
            taste_score: 5,
            appearance_score: 5,
            packaging_score: 4,
            helpful_count: 8,
            not_helpful_count: 0,
          },
          {
            id: '2',
            stars: 4,
            title: 'Good quality',
            body: 'Helps with sleep issues. Would recommend.',
            submitted_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            repeat_purchase: true,
            best_used: 'night',
            scent_score: 4,
            dryness_score: 3,
            taste_score: 4,
            appearance_score: 4,
            packaging_score: 5,
            helpful_count: 5,
            not_helpful_count: 1,
          },
          {
            id: '3',
            stars: 5,
            title: 'Perfect strain',
            body: 'Best product I\'ve tried for anxiety and sleep.',
            submitted_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
            repeat_purchase: true,
            best_used: 'both',
            scent_score: 5,
            dryness_score: 5,
            taste_score: 5,
            appearance_score: 5,
            packaging_score: 5,
            helpful_count: 12,
            not_helpful_count: 0,
          },
        ];

        const mockStats: ReviewStats = {
          total_reviews: mockReviews.length,
          avg_stars: mockReviews.reduce((sum, r) => sum + r.stars, 0) / mockReviews.length,
          star_distribution: {
            5: 2,
            4: 1,
            3: 0,
            2: 0,
            1: 0,
          },
          repeat_purchase_yes: 3,
          repeat_purchase_no: 0,
          best_used_distribution: { day: 0, night: 2, both: 1 },
          avg_scent_score: 4.7,
          avg_dryness_score: 4.0,
          avg_taste_score: 4.7,
          avg_appearance_score: 4.7,
          avg_packaging_score: 4.7,
        };

        const mockTimeline: TimelineDataPoint[] = [
          { date: '6 months ago', avg_rating: 4.5, review_count: 3 },
          { date: '5 months ago', avg_rating: 4.6, review_count: 5 },
          { date: '4 months ago', avg_rating: 4.7, review_count: 7 },
          { date: '3 months ago', avg_rating: 4.8, review_count: 4 },
          { date: '2 months ago', avg_rating: 4.7, review_count: 6 },
          { date: 'Last month', avg_rating: 4.8, review_count: 8 },
        ];

        setProduct(mockProduct);
        setReviews(mockReviews);
        setStats(mockStats);
        setTimelineData(mockTimeline);
        setLoading(false);
        return;
      }

      // Fetch product details
      const { data: productData, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .single();

      if (productError) throw productError;

      // Verify this product belongs to the clinic
      const { data: orgProduct, error: orgError } = await supabase
        .from('org_products')
        .select('*')
        .eq('product_id', productId)
        .eq('org_id', profile.clinic_id)
        .single();

      if (orgError || !orgProduct) {
        console.error('Product not associated with clinic');
        setLoading(false);
        return;
      }

      setProduct(productData);

      // Fetch all reviews for this product
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('product_id', productId)
        .eq('mod_state', 'approved')
        .order('submitted_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      setReviews(reviewsData || []);

      // Calculate stats
      if (reviewsData && reviewsData.length > 0) {
        const starDist: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        let repeatYes = 0, repeatNo = 0;
        let dayCount = 0, nightCount = 0, bothCount = 0;
        let scentSum = 0, drynessSum = 0, tasteSum = 0, appearanceSum = 0, packagingSum = 0;
        let scentCount = 0, drynessCount = 0, tasteCount = 0, appearanceCount = 0, packagingCount = 0;

        reviewsData.forEach(review => {
          starDist[review.stars] = (starDist[review.stars] || 0) + 1;
          
          if (review.repeat_purchase === true) repeatYes++;
          if (review.repeat_purchase === false) repeatNo++;
          
          if (review.best_used === 'day') dayCount++;
          if (review.best_used === 'night') nightCount++;
          if (review.best_used === 'both') bothCount++;

          if (review.scent_score) { scentSum += review.scent_score; scentCount++; }
          if (review.dryness_score) { drynessSum += review.dryness_score; drynessCount++; }
          if (review.taste_score) { tasteSum += review.taste_score; tasteCount++; }
          if (review.appearance_score) { appearanceSum += review.appearance_score; appearanceCount++; }
          if (review.packaging_score) { packagingSum += review.packaging_score; packagingCount++; }
        });

        const calculatedStats: ReviewStats = {
          total_reviews: reviewsData.length,
          avg_stars: reviewsData.reduce((sum, r) => sum + r.stars, 0) / reviewsData.length,
          star_distribution: starDist,
          repeat_purchase_yes: repeatYes,
          repeat_purchase_no: repeatNo,
          best_used_distribution: { day: dayCount, night: nightCount, both: bothCount },
          avg_scent_score: scentCount > 0 ? scentSum / scentCount : null,
          avg_dryness_score: drynessCount > 0 ? drynessSum / drynessCount : null,
          avg_taste_score: tasteCount > 0 ? tasteSum / tasteCount : null,
          avg_appearance_score: appearanceCount > 0 ? appearanceSum / appearanceCount : null,
          avg_packaging_score: packagingCount > 0 ? packagingSum / packagingCount : null,
        };

        setStats(calculatedStats);

        // Generate timeline data (group by month)
        const timeline: { [key: string]: { sum: number; count: number } } = {};
        reviewsData.forEach(review => {
          const date = new Date(review.submitted_at);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!timeline[monthKey]) {
            timeline[monthKey] = { sum: 0, count: 0 };
          }
          timeline[monthKey].sum += review.stars;
          timeline[monthKey].count++;
        });

        const timelineArray: TimelineDataPoint[] = Object.entries(timeline)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, data]) => ({
            date: new Date(date + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
            avg_rating: data.sum / data.count,
            review_count: data.count,
          }));

        setTimelineData(timelineArray);
      }

      // Check for existing certificate
      // In a real implementation, you'd have a certificates table or storage reference
      // For now, we'll just set it to null
      setCertificate(null);

    } catch (error) {
      console.error('Error loading product data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCertificateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !productId) return;

    try {
      setUploadingCert(true);

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${productId}-certificate-${Date.now()}.${fileExt}`;
      const filePath = `certificates/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('clinic-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('clinic-assets')
        .getPublicUrl(filePath);

      setCertificate({
        url: urlData.publicUrl,
        name: file.name,
        uploaded_at: new Date().toISOString(),
        file_size: file.size,
      });

      console.log('Certificate uploaded successfully');
    } catch (error) {
      console.error('Error uploading certificate:', error);
    } finally {
      setUploadingCert(false);
    }
  };

  const getGrade = (avgStars: number, reviewCount: number): string => {
    if (reviewCount === 0) return 'N/A';
    if (avgStars >= 4.8) return 'A+';
    if (avgStars >= 4.5) return 'A';
    if (avgStars >= 4.0) return 'B';
    if (avgStars >= 3.0) return 'C';
    return 'D';
  };

  const getGradeColor = (grade: string): string => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-primary bg-primary/5 border-primary/20';
      case 'B':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'C':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'D':
        return 'text-destructive bg-destructive/5 border-destructive/20';
      default:
        return 'text-muted-foreground bg-muted/50 border-muted';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateLong = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const calculateDaysSinceListing = (): number => {
    if (!product) return 0;
    const now = new Date();
    const created = new Date(product.created_at);
    return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  };

  const COLORS = ['#2d7a4f', '#4a9d6e', '#69b88d', '#88d3ab', '#a7eeca'];

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <Skeleton className="h-8 w-48 mb-8 skeleton-shimmer" />
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(i => (
              <Card key={i} className="border-border/50 shadow-sm">
                <CardHeader>
                  <Skeleton className="h-5 w-32 skeleton-shimmer" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-24 skeleton-shimmer" />
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6">
              <Skeleton className="h-64 w-full skeleton-shimmer" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="mt-16">
            <Alert className="border-border/50 shadow-sm">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>
                Product not found or you don't have access to view this product.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  const grade = getGrade(stats?.avg_stars || 0, stats?.total_reviews || 0);
  const repeatPurchaseRate = stats 
    ? (stats.repeat_purchase_yes + stats.repeat_purchase_no > 0
      ? Math.round((stats.repeat_purchase_yes / (stats.repeat_purchase_yes + stats.repeat_purchase_no)) * 100)
      : 0)
    : 0;

  return (
    <div className="min-h-screen bg-background pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header */}
        <div className="mb-10">
          <Button
            variant="ghost"
            onClick={() => window.location.hash = '#/clinic-dashboard'}
            className="mb-4 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1>{product.product_name}</h1>
                <Badge variant="outline" className={getGradeColor(grade)}>
                  Grade {grade}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Package className="w-4 h-4" />
                  SKU: {product.sku}
                </span>
                {product.brand && (
                  <span>• {product.brand}</span>
                )}
                <span>• {product.form}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Star className="w-4 h-4 text-primary" />
                </div>
                <span>Average Rating</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <div className="stat-number">{stats?.avg_stars.toFixed(1) || '0.0'}</div>
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              </div>
              <p className="text-muted-foreground mt-1">{stats?.total_reviews || 0} reviews</p>
            </CardContent>
          </Card>

          <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ThumbsUp className="w-4 h-4 text-primary" />
                </div>
                <span>Would Buy Again</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <div className="stat-number text-primary">{repeatPurchaseRate}%</div>
                {repeatPurchaseRate >= 70 ? (
                  <TrendingUp className="w-5 h-5 text-primary" />
                ) : repeatPurchaseRate < 50 ? (
                  <TrendingDown className="w-5 h-5 text-destructive" />
                ) : null}
              </div>
              <p className="text-muted-foreground mt-1">
                {stats?.repeat_purchase_yes || 0} of {(stats?.repeat_purchase_yes || 0) + (stats?.repeat_purchase_no || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <span>Listed Since</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="stat-number">{calculateDaysSinceListing()}</div>
              <p className="text-muted-foreground mt-1">days ago</p>
            </CardContent>
          </Card>

          <Card className="border-border/50 shadow-sm hover-lift transition-smooth">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Users className="w-4 h-4 text-amber-500" />
                </div>
                <span>Total Reviews</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="stat-number">{stats?.total_reviews || 0}</div>
              <p className="text-muted-foreground mt-1">
                {reviews.length > 0 && `Last: ${formatDate(reviews[0].submitted_at)}`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Product Details */}
        <Card className="border-border/50 shadow-sm mb-8">
          <CardHeader>
            <CardTitle>Product Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-muted-foreground mb-1">THC Content</p>
                <p className="stat-number">{product.thc_pct ? `${product.thc_pct}%` : 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">CBD Content</p>
                <p className="stat-number">{product.cbd_pct ? `${product.cbd_pct}%` : 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Pack Size</p>
                <p>{product.pack_size || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Form</p>
                <p className="capitalize">{product.form}</p>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Status</p>
                <Badge variant={product.active ? "outline" : "secondary"} className={product.active ? "text-primary bg-primary/5 border-primary/20" : ""}>
                  {product.active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Listed On</p>
                <p>{formatDateLong(product.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Analytics */}
        <Tabs defaultValue="analytics" className="mb-8">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="reviews">
              <Star className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="certificate">
              <FileText className="w-4 h-4 mr-2" />
              Certificate
            </TabsTrigger>
          </TabsList>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6 mt-6">
            {/* Rating Timeline */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle>Rating Timeline</CardTitle>
                <CardDescription>Average rating trend over time</CardDescription>
              </CardHeader>
              <CardContent>
                {timelineData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="date" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis 
                        domain={[0, 5]} 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '0.5rem'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="avg_rating" 
                        stroke="#2d7a4f" 
                        strokeWidth={2}
                        dot={{ fill: '#2d7a4f', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No timeline data available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Star Distribution */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle>Star Rating Distribution</CardTitle>
                <CardDescription>Breakdown of all ratings received</CardDescription>
              </CardHeader>
              <CardContent>
                {stats && stats.total_reviews > 0 ? (
                  <div className="space-y-4">
                    {[5, 4, 3, 2, 1].map(stars => {
                      const count = stats.star_distribution[stars] || 0;
                      const percentage = (count / stats.total_reviews) * 100;
                      
                      return (
                        <div key={stars} className="flex items-center gap-4">
                          <div className="flex items-center gap-1 w-16">
                            <span className="stat-number">{stars}</span>
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                          </div>
                          <Progress value={percentage} className="flex-1 h-3" />
                          <span className="stat-number w-16 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No ratings yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quality Scores */}
            {stats && (stats.avg_scent_score || stats.avg_taste_score || stats.avg_appearance_score) && (
              <Card className="border-border/50 shadow-sm">
                <CardHeader>
                  <CardTitle>Quality Scores</CardTitle>
                  <CardDescription>Average scores across different attributes</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {stats.avg_scent_score && (
                      <div>
                        <p className="text-muted-foreground mb-2">Scent</p>
                        <div className="flex items-baseline gap-2">
                          <div className="stat-number">{stats.avg_scent_score.toFixed(1)}</div>
                          <span className="text-muted-foreground">/5</span>
                        </div>
                      </div>
                    )}
                    {stats.avg_dryness_score && (
                      <div>
                        <p className="text-muted-foreground mb-2">Dryness</p>
                        <div className="flex items-baseline gap-2">
                          <div className="stat-number">{stats.avg_dryness_score.toFixed(1)}</div>
                          <span className="text-muted-foreground">/5</span>
                        </div>
                      </div>
                    )}
                    {stats.avg_taste_score && (
                      <div>
                        <p className="text-muted-foreground mb-2">Taste</p>
                        <div className="flex items-baseline gap-2">
                          <div className="stat-number">{stats.avg_taste_score.toFixed(1)}</div>
                          <span className="text-muted-foreground">/5</span>
                        </div>
                      </div>
                    )}
                    {stats.avg_appearance_score && (
                      <div>
                        <p className="text-muted-foreground mb-2">Appearance</p>
                        <div className="flex items-baseline gap-2">
                          <div className="stat-number">{stats.avg_appearance_score.toFixed(1)}</div>
                          <span className="text-muted-foreground">/5</span>
                        </div>
                      </div>
                    )}
                    {stats.avg_packaging_score && (
                      <div>
                        <p className="text-muted-foreground mb-2">Packaging</p>
                        <div className="flex items-baseline gap-2">
                          <div className="stat-number">{stats.avg_packaging_score.toFixed(1)}</div>
                          <span className="text-muted-foreground">/5</span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Best Used */}
            {stats && (stats.best_used_distribution.day > 0 || stats.best_used_distribution.night > 0 || stats.best_used_distribution.both > 0) && (
              <Card className="border-border/50 shadow-sm">
                <CardHeader>
                  <CardTitle>Best Used</CardTitle>
                  <CardDescription>When patients prefer to use this product</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="stat-number mb-1">{stats.best_used_distribution.day}</div>
                      <p className="text-muted-foreground">Daytime</p>
                    </div>
                    <div className="text-center">
                      <div className="stat-number mb-1">{stats.best_used_distribution.night}</div>
                      <p className="text-muted-foreground">Nighttime</p>
                    </div>
                    <div className="text-center">
                      <div className="stat-number mb-1">{stats.best_used_distribution.both}</div>
                      <p className="text-muted-foreground">Both</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle>All Reviews</CardTitle>
                <CardDescription>Patient feedback and experiences</CardDescription>
              </CardHeader>
              <CardContent>
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map(review => (
                      <div key={review.id} className="border-b border-border/50 pb-6 last:border-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.stars
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-muted-foreground/30'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-muted-foreground">{formatDate(review.submitted_at)}</span>
                        </div>

                        {review.title && (
                          <h4 className="mb-2">{review.title}</h4>
                        )}

                        {review.body && (
                          <p className="text-muted-foreground mb-3">{review.body}</p>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {review.repeat_purchase === true && (
                            <Badge variant="outline" className="text-primary bg-primary/5 border-primary/20">
                              <ThumbsUp className="w-3 h-3 mr-1" />
                              Would buy again
                            </Badge>
                          )}
                          {review.repeat_purchase === false && (
                            <Badge variant="outline" className="text-destructive bg-destructive/5 border-destructive/20">
                              <ThumbsDown className="w-3 h-3 mr-1" />
                              Would not buy again
                            </Badge>
                          )}
                          {review.best_used && (
                            <Badge variant="outline">
                              <Clock className="w-3 h-3 mr-1" />
                              Best for {review.best_used}
                            </Badge>
                          )}
                        </div>

                        {(review.scent_score || review.taste_score || review.appearance_score) && (
                          <div className="flex flex-wrap gap-4 mt-3 text-sm">
                            {review.scent_score && (
                              <span className="text-muted-foreground">Scent: {review.scent_score}/5</span>
                            )}
                            {review.taste_score && (
                              <span className="text-muted-foreground">Taste: {review.taste_score}/5</span>
                            )}
                            {review.appearance_score && (
                              <span className="text-muted-foreground">Appearance: {review.appearance_score}/5</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No reviews yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certificate Tab */}
          <TabsContent value="certificate" className="mt-6">
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle>Product Certificate</CardTitle>
                <CardDescription>Upload certificate of analysis or compliance documents</CardDescription>
              </CardHeader>
              <CardContent>
                {certificate ? (
                  <div className="space-y-4">
                    <Alert className="border-primary/20 bg-primary/5">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <AlertDescription>
                        <strong className="text-primary">Certificate uploaded</strong>
                        <p className="text-muted-foreground mt-1">
                          {certificate.name} • Uploaded {formatDate(certificate.uploaded_at)}
                        </p>
                      </AlertDescription>
                    </Alert>

                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => window.open(certificate.url, '_blank')}>
                        <FileText className="w-4 h-4 mr-2" />
                        View Certificate
                      </Button>
                      <Button variant="outline" onClick={() => setCertificate(null)}>
                        Replace Certificate
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="mb-2">No certificate uploaded</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Upload a certificate of analysis, compliance certificate, or other relevant documentation
                    </p>
                    <label htmlFor="certificate-upload">
                      <Button variant="outline" className="transition-smooth hover-lift" disabled={uploadingCert}>
                        {uploadingCert ? (
                          <>
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Certificate
                          </>
                        )}
                      </Button>
                      <input
                        id="certificate-upload"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleCertificateUpload}
                        className="hidden"
                        disabled={uploadingCert}
                      />
                    </label>
                    <p className="text-muted-foreground mt-4">Accepted formats: PDF, JPG, PNG</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
